import React from 'react';
import { AssistantViewBaseConfig } from '../../../base';
import { ThreadOverviewViewProps } from './threadOverviewProps';
import {
    HStack,
    VStack,
    Box,
    StackDivider,
    Grid,
    GridItem,
    Text,
    Image,
    Checkbox,
    Button,
    Icon,
    BoxProps,
    Badge,
    Link as ChakraLink,
    Tooltip,
    useToken,
    Heading,
    Stack,
    ButtonProps,
} from '@chakra-ui/react';
import { Graphics } from '../../../../../config/svg';
import {
    AiOutlineClockCircle,
    AiOutlineInfoCircle,
    AiOutlineCheckCircle,
} from 'react-icons/ai';
import {
    MdOpenInNew,
    MdOutlineRadioButtonChecked,
    MdOutlineRadioButtonUnchecked,
} from 'react-icons/md';
import { ThreadItemProps } from '../item';
import { IconType } from 'react-icons';

export function createThreadOverviewView(
    config: AssistantViewBaseConfig
): React.FC<ThreadOverviewViewProps> {
    const {
        Layout,
        UI: {
            Application: { Link, Tooltip, Anchor },
        },
    } = config;

    const containerStyle = {
        maxW: '56rem',
    } satisfies BoxProps;

    const EmptyState: React.FC<Exclude<ThreadOverviewViewProps['empty'], null>> = (
        props
    ) => {
        return (
            <VStack
                w="full"
                align="center"
                spacing={4}
                py={{ base: 8, md: 12 }}
                px={{ base: 8, md: 12 }}
                borderRadius="lg"
                borderWidth={2}
                borderColor="whiteAlpha.200"
            >
                <Icon
                    as={props.Icon}
                    w={{ base: 8, md: 12 }}
                    h={{ base: 8, md: 12 }}
                    color={`${props.colorScheme}.300`}
                />
                <VStack spacing={2} textAlign="center">
                    <Text fontSize="lg" fontWeight="semibold" color="whiteAlpha.900">
                        {props.title}
                    </Text>
                    <Text fontSize="md" color="whiteAlpha.600" maxW="28rem">
                        {props.description}
                    </Text>
                </VStack>
            </VStack>
        );
    };

    const ThreadItem: React.FC<ThreadItemProps> = (props) => {
        const status = (
            <Tooltip
                {...props.status.tooltip}
                placement="top"
                openDelay={300}
                px={2}
                py={1}
                bg="gray.50"
                color="gray.900"
                borderRadius="md"
                closeOnClick={false}
                shouldWrapChildren={true}
            >
                <Badge
                    colorScheme={props.status.colorScheme}
                    variant="subtle"
                    fontSize="xs"
                    px={2}
                    py={1}
                >
                    <HStack spacing={1.5} userSelect="none">
                        <Box
                            borderRadius="full"
                            w={2}
                            h={2}
                            bg={`${props.status.colorScheme}.200`}
                        />
                        <Text>{props.status.label}</Text>
                    </HStack>
                </Badge>
            </Tooltip>
        );
        return (
            <Link style={{ width: '100%' }} {...props.link}>
                <Box
                    w="full"
                    p={{ base: 4, md: 6 }}
                    _hover={{
                        bg: 'whiteAlpha.50',
                    }}
                >
                    <Stack
                        w="full"
                        direction={{ base: 'column', md: 'row' }}
                        justify={{ base: 'start', md: 'space-between' }}
                        align={{ base: 'stretch', md: 'center' }}
                        spacing={{ base: 2, md: 5 }}
                    >
                        {/* Top row on mobile: Label left, Status right */}
                        <HStack
                            w="full"
                            justify={{ base: 'space-between', md: 'start' }}
                            align="center"
                        >
                            <HStack spacing={3} align="center">
                                {/* <Icon as={props.Icon} w={4} h={4} color="whiteAlpha.900" /> */}
                                <Text
                                    fontSize="md"
                                    fontWeight="medium"
                                    color="whiteAlpha.900"
                                >
                                    {props.label}
                                </Text>
                            </HStack>

                            {/* Status - show on mobile top row, hide on desktop (will show in second section) */}
                            <Box display={{ base: 'block', md: 'none' }}>{status}</Box>
                        </HStack>

                        {/* Bottom row on mobile: Timestamp and metadata / Right side on desktop: Timestamp + Status */}
                        <HStack
                            spacing={{ base: 2, md: 5 }}
                            align="center"
                            justify={{ base: 'start', md: 'end' }}
                            flexShrink={0}
                        >
                            <Text fontSize="sm" color="whiteAlpha.500">
                                {props.timestamp}
                            </Text>

                            {/* Status - hide on mobile (shown above), show on desktop */}
                            <Box display={{ base: 'none', md: 'block' }}>{status}</Box>
                        </HStack>
                    </Stack>
                </Box>
            </Link>
        );
    };

    return (props) => {
        const [gradientStart, gradientEnd] = useToken('colors', [
            'whiteAlpha.50',
            'whiteAlpha.200',
        ]);

        return (
            <Layout title={undefined}>
                <HStack w="full" justify="center" pb={{ base: 24, md: 32 }}>
                    <VStack
                        maxW={containerStyle.maxW}
                        align="center"
                        w="full"
                        p={{ base: 4, md: 6 }}
                        spacing={{ base: 4, md: 12 }}
                    >
                        <HStack w="full" justify="space-between">
                            <HStack>
                                <Heading
                                    fontWeight="medium"
                                    fontSize={{ base: 'xl', md: '2xl' }}
                                >
                                    Interviews
                                </Heading>
                            </HStack>
                            <HStack display={{ base: 'none', md: 'flex' }}>
                                {props.hero.action.learn && (
                                    <Button
                                        {...props.hero.action.learn.button}
                                        variant="outline"
                                        borderColor="whiteAlpha.300"
                                        bg="none"
                                        _hover={{ bg: 'whiteAlpha.50' }}
                                        rightIcon={<Icon as={MdOpenInNew} />}
                                    >
                                        {props.hero.action.learn.label}
                                    </Button>
                                )}
                            </HStack>
                        </HStack>
                        <VStack align="start" w="full" maxW={containerStyle.maxW}>
                            {/* Thread List Section */}
                            {(props.thread.pending.length > 0 ||
                                props.thread.completed.length > 0) && (
                                <VStack w="full" align="start" spacing={6}>
                                    {/* Pending Interviews Section */}
                                    {props.thread.pending.length > 0 && (
                                        <VStack w="full" align="start" spacing={4}>
                                            <HStack>
                                                <Text
                                                    fontSize="md"
                                                    fontWeight="semibold"
                                                    color="whiteAlpha.500"
                                                >
                                                    Pending
                                                </Text>
                                            </HStack>
                                            <VStack
                                                w="full"
                                                spacing={0}
                                                divider={
                                                    <StackDivider
                                                        borderWidth={1}
                                                        borderColor="whiteAlpha.200"
                                                    />
                                                }
                                                borderRadius="lg"
                                                borderWidth={2}
                                                borderColor="whiteAlpha.200"
                                            >
                                                {props.thread.pending.map((item) => (
                                                    <ThreadItem key={item.id} {...item} />
                                                ))}
                                            </VStack>
                                        </VStack>
                                    )}

                                    {/* All Caught Up Hero Section */}
                                    {props.thread.pending.length === 0 && props.empty && (
                                        <EmptyState {...props.empty} />
                                    )}

                                    {/* Completed Interviews Section */}
                                    {props.thread.completed.length > 0 && (
                                        <VStack w="full" align="start" spacing={4}>
                                            <HStack>
                                                <Text
                                                    fontSize="md"
                                                    fontWeight="semibold"
                                                    color="whiteAlpha.500"
                                                >
                                                    Completed
                                                </Text>
                                            </HStack>
                                            <VStack
                                                w="full"
                                                spacing={0}
                                                divider={
                                                    <StackDivider
                                                        borderWidth={1}
                                                        borderColor="whiteAlpha.200"
                                                    />
                                                }
                                                borderRadius="lg"
                                                borderWidth={2}
                                                borderColor="whiteAlpha.200"
                                            >
                                                {props.thread.completed.map((item) => (
                                                    <ThreadItem key={item.id} {...item} />
                                                ))}
                                            </VStack>
                                        </VStack>
                                    )}
                                </VStack>
                            )}

                            {/* Empty State */}
                            {props.thread.pending.length === 0 &&
                                props.thread.completed.length === 0 &&
                                props.empty && <EmptyState {...props.empty} />}
                        </VStack>
                    </VStack>
                </HStack>
            </Layout>
        );
    };
}
